import { rigListQuerySchema, type RigListItem } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { listRigs } from '../features/units/units.api';

/** `rig` is null for a deactivated trailer no truck is pulling: it isn't a unit the office can send out. */
export type VehicleRigState = { status: 'loading' } | { status: 'loaded'; rig: RigListItem | null } | { status: 'error' };

function fetchRig(vehicleKind: 'truck' | 'trailer', vehicleId: string): Promise<RigListItem | null> {
  const filter = vehicleKind === 'truck' ? { truckId: [vehicleId] } : { trailerId: [vehicleId] };
  return listRigs({ ...rigListQuerySchema.parse({ pageSize: 1 }), ...filter }).then((page) => page.items[0] ?? null);
}

/** The rig a truck or trailer is part of now (gitaumoses4/chukua#390), for the header of its own page. */
export function useVehicleRig(vehicleKind: 'truck' | 'trailer', vehicleId: string) {
  const [state, setState] = useState<VehicleRigState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchRig(vehicleKind, vehicleId)
      .then((rig) => {
        if (!cancelled) setState({ status: 'loaded', rig });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [vehicleKind, vehicleId]);

  /** After a couple or uncouple commits: a plain async function, not tied to the effect above. */
  const reload = useCallback(async () => {
    try {
      setState({ status: 'loaded', rig: await fetchRig(vehicleKind, vehicleId) });
    } catch {
      setState({ status: 'error' });
    }
  }, [vehicleKind, vehicleId]);

  return { state, reload };
}
